import { h, FunctionComponent } from 'preact';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import InputGroup from '~src/domains/common/components/InputGroup';
import SelectGroup from '~src/domains/common/components/SelectGroup';
import { Page } from '~src/domains/common/enums/api';
import {
  Brand,
  InputGroupWidth,
  QuebecOption,
} from '~src/domains/common/enums/common';
import { RegionsCA } from '~src/domains/common/enums/localization';
import {
  brandSelector,
  optionsSelector,
} from '~src/domains/common/state/commonSelectors';
import * as invalidAddressFormActions from '~src/domains/invalidAddress/state/invalidAddressFormReducer';
import * as styles from '~src/styles/domains/invalidAddress/InvalidAddressPage.module.scss';
import { getOptionsList } from '~src/utils/helpers';
import { InvalidAddressOption } from '../enums/invalidAddress';
import { invalidAddressFormSelector } from '../state/invalidAddressSelectors';

const getInputGroupClass = (width?: string) => {
  return width === InputGroupWidth.HALF
    ? styles['invalid-address-input-half']
    : width === InputGroupWidth.HALF_DESKTOP
    ? styles['invalid-address-input-half-desktop']
    : '';
};

const InvalidAddressInputs: FunctionComponent = () => {
  const { t } = useTranslation();
  const options = useSelector(optionsSelector) as InvalidAddressOption[];
  const brand = useSelector(brandSelector);
  const isUplift = brand == Brand.UPLIFT;
  // Convert array of options into a map. (i.e ['address', 'city'] -> { address: true, city: true })
  const mappedOptions = options?.reduce(
    (prev: { [key: string]: boolean }, curr: InvalidAddressOption) => {
      prev[curr] = true;
      return prev;
    },
    {},
  );
  const invalidAddressForm = useSelector(invalidAddressFormSelector);
  const showQuebecFields = invalidAddressForm.region.value === RegionsCA.QC;
  const inputs = t('invalid-address:inputs', {
    returnObjects: true,
  }) as invalidAddressFormActions.InvalidAddressLocalization;
  const regions =
    t('common:regions', {
      returnObjects: true,
    }) || {};
  const incomeSources =
    (showQuebecFields &&
      t('invalid-address:incomeSources', { returnObjects: true })) ||
    {};
  const employmentDurations =
    (showQuebecFields &&
      t('invalid-address:employmentDurations', {
        returnObjects: true,
      })) ||
    {};
  const regionOptions = getOptionsList(regions, true);
  const incomeSourceOptions = getOptionsList(incomeSources);
  const employmentDurationOptions = getOptionsList(employmentDurations);

  const inputGroup = (
    invalidAddressForm: { [k: string]: any },
    key: string,
    optionalConfig?: {
      width?: InputGroupWidth;
      groupType?: string;
      extras?: string[];
      onChangePost?: (e: any) => void;
    },
    options?: any[],
  ) => {
    const sharedProps = {
      extras: optionalConfig?.extras || [],
      page: Page.INVALID_ADDRESS,
      id: key,
      input: inputs[key],
      state: invalidAddressForm[key],
      reduxActions: invalidAddressFormActions,
    };

    return (
      <div className={getInputGroupClass(optionalConfig?.width)}>
        {options ? (
          <SelectGroup
            {...sharedProps}
            onChangePost={optionalConfig?.onChangePost}
            options={options}
          />
        ) : (
          <InputGroup
            {...sharedProps}
            groupType={optionalConfig?.groupType || ''}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <div className={styles['invalid-address-inputs']}>
        {mappedOptions[InvalidAddressOption.ADDRESS] &&
          inputGroup(invalidAddressForm, InvalidAddressOption.ADDRESS)}
        {mappedOptions[InvalidAddressOption.CITY] &&
          inputGroup(invalidAddressForm, InvalidAddressOption.CITY)}
        {mappedOptions[InvalidAddressOption.REGION] &&
          inputGroup(
            invalidAddressForm,
            InvalidAddressOption.REGION,
            {
              width: isUplift
                ? InputGroupWidth.HALF
                : InputGroupWidth.HALF_DESKTOP,
            },
            regionOptions,
          )}
        {mappedOptions[InvalidAddressOption.POSTAL_CODE] &&
          inputGroup(invalidAddressForm, InvalidAddressOption.POSTAL_CODE, {
            width: isUplift
              ? InputGroupWidth.HALF
              : InputGroupWidth.HALF_DESKTOP,
          })}
        {showQuebecFields && (
          <>
            {inputGroup(invalidAddressForm, QuebecOption.MONTHLY_HOUSING_COST, {
              width: InputGroupWidth.HALF_DESKTOP,
              extras: ['currencyConfig'],
            })}
            {inputGroup(
              invalidAddressForm,
              QuebecOption.PRIMARY_INCOME_SOURCE,
              {
                width: InputGroupWidth.HALF_DESKTOP,
              },
              incomeSourceOptions,
            )}
            {inputGroup(invalidAddressForm, QuebecOption.OCCUPATION, {
              width: InputGroupWidth.HALF_DESKTOP,
            })}
            {inputGroup(
              invalidAddressForm,
              QuebecOption.EMPLOYMENT_DURATION,
              {
                width: InputGroupWidth.HALF_DESKTOP,
              },
              employmentDurationOptions,
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InvalidAddressInputs;
